import {OnDestroy, Pipe, PipeTransform} from '@angular/core';
import {Subject} from 'rxjs';
import {filter, map, takeUntil} from 'rxjs/operators';
import {Apollo} from 'apollo-angular';
import {File, Query} from '../types-repository';
import {FILE_ARCHIVE_STATE} from '../queries-repository';
import {RemainingTimePipe} from './remaining-time-pipe';

@Pipe({
    name: 'estimatedRestorationTime',
    pure: true // Now pure! No more repeated queries
})
export class EstimatedRestorationTimePipe implements PipeTransform, OnDestroy {
  private etaCache = new Map<string, string>(); // fileId -> ETA datetime
  private fetchingFiles = new Set<string>();
  private destroy$ = new Subject<void>();
  private remainingTimePipe = new RemainingTimePipe();

  constructor(private apollo: Apollo) {
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.etaCache.clear();
    this.fetchingFiles.clear();
  }

  transform(file: File): string {
    if (!file?.id) {
      return 'Unknown';
    }

    // Check if we have a cached ETA for this file
    const cachedEta = this.etaCache.get(file.id);

    if (cachedEta) {
      // We have cached ETA datetime, just calculate remaining time
      return this.remainingTimePipe.transform(cachedEta, 'almost done') || 'almost done';
    }

    // If we're already fetching this file, return "Calculating..."
    if (this.fetchingFiles.has(file.id)) {
      return 'Calculating...';
    }

    // Start fetching for this file (only once)
    this.fetchingFiles.add(file.id);

    this.apollo.query<Query>({
      query: FILE_ARCHIVE_STATE,
      variables: {
        id: file.id
      }
    }).pipe(
      filter(d => !!d.data?.file),
      map(d => d.data.file),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (fileData) => {
        this.fetchingFiles.delete(file.id);

        const restoreEta = fileData?.archive?.extra?.restore_eta;

        if (restoreEta) {
          // Store the ETA datetime from the server
          this.etaCache.set(file.id, restoreEta);
        }
      },
      error: (error) => {
        console.error(`Failed to fetch ETA for file ${file.id}:`, error);
        this.fetchingFiles.delete(file.id);
      }
    });

    return 'Calculating...';
  }
}
